import { useCallback, useEffect, useRef, useState } from 'react';

import { USE_MQTT } from '../../../config/mqttConfig';
import { useCrewWebSockets } from '../../../hooks/useCrewWebSockets';
import { WebSocketConnectionState } from '../../../hooks/useWebSocketConnection';
import { SharedSessionParticipant, SharedSessionResponse } from '../../../services/types/api.generated';
import { useCrewMQTT } from './useCrewMQTT';

interface UseCrewViewWebSocketsProps {
  sessionId: string;
  participantId: string;
  deviceUuid: string;
  telemetryUrlBuilder: (pId: string) => string;
  pingUrlBuilder: (pId: string) => string;
  onTelemetryMessage: (data: unknown) => void;
  onPongMessage: (data: SharedSessionResponse & { participants: SharedSessionParticipant[] }) => void;
  onEventMessage?: (event: string, extra: Record<string, unknown>) => void;
  onSessionEnded?: () => void;
  setSessionData: (data: SharedSessionResponse | null) => void;
  setParticipants: (participants: SharedSessionParticipant[]) => void;
  setPingVideoUrl: (url: string | null | undefined) => void;
}

export const useCrewViewWebSockets = ({
  sessionId,
  participantId,
  deviceUuid,
  telemetryUrlBuilder,
  pingUrlBuilder,
  onTelemetryMessage,
  onPongMessage,
  onEventMessage,
  onSessionEnded,
  setSessionData,
  setParticipants,
  setPingVideoUrl
}: UseCrewViewWebSocketsProps) => {
  const previousConnectionStateRef = useRef<{ telemetry: WebSocketConnectionState; ping: WebSocketConnectionState }>({
    telemetry: 'disconnected',
    ping: 'disconnected'
  });
  const isDisconnectingRef = useRef<boolean>(false);
  const isConnectingRef = useRef<boolean>(false);

  const handlePongMessage = useCallback(
    (data: SharedSessionResponse & { participants: SharedSessionParticipant[] }) => {
      if (data) {
        setSessionData(data);
        const normalizedVideoUrl = data.video_url?.trim().replace(/\/$/, '');
        const whepUrl = normalizedVideoUrl ?? null;
        setPingVideoUrl(whepUrl);
      }
      if (data && data.participants) {
        setParticipants(data.participants);
      }
      onPongMessage(data);
    },
    [setSessionData, setParticipants, setPingVideoUrl, onPongMessage]
  );

  const {
    connect: connectMQTT,
    disconnect: disconnectMQTT,
    connectionState: mqttConnectionState
  } = useCrewMQTT({
    deviceUuid,
    onTelemetryMessage,
    onError: (error) => {
      if (error) console.error('Crew MQTT error:', error);
    },
    maxReconnectAttempts: 5
  });

  const {
    connect: connectWebSocketsRaw,
    disconnect: disconnectWebSocketsRaw,
    connectionState: wsConnectionState
  } = useCrewWebSockets({
    telemetryUrlBuilder: USE_MQTT ? () => '' : telemetryUrlBuilder,
    pingUrlBuilder,
    onTelemetryMessage: USE_MQTT ? () => {} : onTelemetryMessage,
    onPongMessage: handlePongMessage,
    onEventMessage,
    onError: (error) => {
      if (error) console.error('Crew WebSocket error:', error);
    },
    onSessionEnded: () => {
      onSessionEnded?.();
    },
    maxReconnectAttempts: 5,
    sessionId,
    deviceUuid
  });

  const connectWebSockets = useCallback(
    (pId: string) => {
      if (!pId || pId.trim() === '') {
        console.error('Cannot connect WebSockets: participantId is required');
        return;
      }
      connectWebSocketsRaw(pId);
    },
    [connectWebSocketsRaw]
  );

  const disconnectWebSockets = useCallback(() => {
    disconnectWebSocketsRaw();
  }, [disconnectWebSocketsRaw]);

  const connectionState: { telemetry: WebSocketConnectionState; ping: WebSocketConnectionState } = USE_MQTT
    ? {
        telemetry: mqttConnectionState,
        ping: wsConnectionState.ping
      }
    : wsConnectionState;

  useEffect(() => {
    if (isDisconnectingRef.current) {
      return;
    }

    const prevState = previousConnectionStateRef.current;
    const currentState = connectionState;

    if (prevState.telemetry === currentState.telemetry && prevState.ping === currentState.ping) {
      return;
    }

    const wasNeverConnected =
      prevState.telemetry === 'disconnected' &&
      prevState.ping === 'disconnected' &&
      currentState.telemetry === 'disconnected' &&
      currentState.ping === 'disconnected';

    if (wasNeverConnected) {
      previousConnectionStateRef.current = {
        telemetry: currentState.telemetry,
        ping: currentState.ping
      };
      return;
    }

    const telemetryWasConnected = prevState.telemetry === 'connected';
    const telemetryNowDisconnected = currentState.telemetry === 'disconnected' || currentState.telemetry === 'error';

    const pingWasConnected = prevState.ping === 'connected';
    const pingNowDisconnected = currentState.ping === 'disconnected' || currentState.ping === 'error';

    const isRetrying = currentState.telemetry === 'error' || currentState.ping === 'error';
    const shouldDisconnect =
      (telemetryWasConnected && telemetryNowDisconnected) || (pingWasConnected && pingNowDisconnected);

    if (shouldDisconnect && !isRetrying) {
      isDisconnectingRef.current = true;
      if (USE_MQTT) {
        disconnectMQTT();
      }
      disconnectWebSockets();
      setTimeout(() => {
        isDisconnectingRef.current = false;
      }, 100);
    }

    // Update previous state
    previousConnectionStateRef.current = {
      telemetry: currentState.telemetry,
      ping: currentState.ping
    };
  }, [connectionState, disconnectWebSockets, disconnectMQTT]);

  const connect = useCallback(async () => {
    if (isConnectingRef.current) {
      return;
    }

    isConnectingRef.current = true;
    try {
      if (USE_MQTT) {
        connectMQTT();
      }
      connectWebSockets(participantId);
    } catch (error) {
      console.error('Failed to connect:', error);
    } finally {
      setTimeout(() => {
        isConnectingRef.current = false;
      }, 1000);
    }
  }, [USE_MQTT, connectMQTT, connectWebSockets, participantId]);

  const disconnect = useCallback(() => {
    isDisconnectingRef.current = true;
    if (USE_MQTT) {
      disconnectMQTT();
    }
    disconnectWebSockets();
    setTimeout(() => {
      isDisconnectingRef.current = false;
    }, 100);
  }, [disconnectMQTT, disconnectWebSockets]);

  return {
    connect,
    disconnect,
    connectionState
  };
};
